import { Form } from 'react-bootstrap';
import { ConfigurationRow } from '@blisspointmedia/bpm-types/dist/SiteMonitoring';
import { Button } from '../../Components/Button';
import { Appearance, Placement, Size } from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { InfoTooltip } from '../../Components/InfoTooltip';

export const FREQUENCIES = ['30 Min', 'Hourly', 'Daily'];

export const FormValueKey = {
  COMPANY: 'company',
  PAGE_URL: 'pageUrl',
  SLACK_CHANNEL: 'slackChannel',
  DRI: 'dri',
  FREQUENCY: 'frequency'
} as const;

export interface FormValues {
  [FormValueKey.COMPANY]: string;
  [FormValueKey.PAGE_URL]: string;
  [FormValueKey.SLACK_CHANNEL]: string;
  [FormValueKey.DRI]: string;
  [FormValueKey.FREQUENCY]: string;
}

interface MonitorFormProps {
  mode: 'create' | 'update';
  values: FormValues;
  currentConfiguration?: ConfigurationRow;
  onValueChange: (key: keyof FormValues, value: string) => void;
  onCancel: () => void;
  onCreate: () => void;
  onUpdate: () => void;
}

export const MonitorForm = ({
  mode,
  values,
  currentConfiguration,
  onValueChange,
  onCancel,
  onCreate,
  onUpdate
}: MonitorFormProps) => (
  <>
    <div className='helper-text'>
      <p>
        • Please make sure Slack app <b>AMP-Portal Reporting Bot</b> is added to the channel.
      </p>
      <p>
        • Use the following{' '}
        <a
          target='_blank'
          rel='noopener noreferrer'
          href='https://www.notion.so/Adding-Reporting-Bot-7ffa196bfa0348e794a12a0eb7ae38ee?pvs=4'
        >
          Document
        </a>{' '}
        to learn how to add reporting app to the slack channel.
      </p>
    </div>
    <Form>
      <Form.Group className='form-field'>
        <Form.Label>Company</Form.Label>
        <div className='bottom'>
          <Form.Control
            type='text'
            value={values[FormValueKey.COMPANY]}
            placeholder='Company'
            disabled={mode === 'update'}
            onChange={e => onValueChange(FormValueKey.COMPANY, e.currentTarget.value)}
          />
        </div>
      </Form.Group>
      <Form.Group className='form-field'>
        <Form.Label>Page URL</Form.Label>
        <div className='bottom'>
          <Form.Control
            type='text'
            value={values[FormValueKey.PAGE_URL]}
            placeholder='Page URL'
            onChange={e => onValueChange(FormValueKey.PAGE_URL, e.currentTarget.value)}
          />
        </div>
      </Form.Group>
      <Form.Group className='form-field'>
        <Form.Label>Slack Channel Name</Form.Label>
        <div className='bottom'>
          <Form.Control
            type='text'
            value={values[FormValueKey.SLACK_CHANNEL]}
            placeholder='Slack Channel Name'
            onChange={e => onValueChange(FormValueKey.SLACK_CHANNEL, e.currentTarget.value)}
          />
          <InfoTooltip>
            Add the &apos;-&apos; or &apos;_&apos; in between the Slack channel name, please do not
            include space and # in the Slack channel name.
          </InfoTooltip>
        </div>
      </Form.Group>
      <Form.Group className='form-field'>
        <Form.Label>DRI Slack Username</Form.Label>
        <div className='bottom'>
          <Form.Control
            type='text'
            value={values[FormValueKey.DRI]}
            placeholder='DRI Slack Username'
            onChange={e => onValueChange(FormValueKey.DRI, e.currentTarget.value)}
          />
          <InfoTooltip>
            DRI should include all analysts, leads, directors, and product managers working with a
            client. DRIs will be tagged in Slack message when an alert fires. You can find slack
            Username by navigating to your profile on Slack &gt; dropdown on right hand side &gt;
            copy display name. E.g. sarah.malone.
            <br />
            <br />
            Separate multiple Usernames with using commas.
          </InfoTooltip>
        </div>
      </Form.Group>
      <Form.Group className='form-field'>
        <Form.Label>Frequency</Form.Label>
        <div className='bottom'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            className='frequency-dropdown'
            value={values[FormValueKey.FREQUENCY]}
            options={FREQUENCIES}
            onChange={val => onValueChange(FormValueKey.FREQUENCY, val)}
          />
          <InfoTooltip>
            Suggested frequency is 30 minutes. You may use a lower frequency for very low impact
            pages in order to save marginally on cost of alerting.
          </InfoTooltip>
        </div>
      </Form.Group>
      <div className='form-actions'>
        <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onCancel}>
          Cancel
        </Button>
        {mode === 'create' ? (
          <Button
            size={Size.LG}
            disabled={Object.values(FormValueKey).some(key => !values[key])}
            onClick={onCreate}
          >
            Create
          </Button>
        ) : (
          <Button
            size={Size.LG}
            disabled={
              currentConfiguration?.slack_channel === values[FormValueKey.SLACK_CHANNEL] &&
              currentConfiguration?.frequency === values[FormValueKey.FREQUENCY] &&
              currentConfiguration?.dri === values[FormValueKey.DRI] &&
              currentConfiguration?.page_url === values[FormValueKey.PAGE_URL]
            }
            onClick={onUpdate}
          >
            Update
          </Button>
        )}
      </div>
    </Form>
  </>
);
